/**
 * Bid Application Service
 * Transforms bid data from backend DTOs into UI-ready view models
 *
 * Following DDD Principles:
 * - Pure transformation layer (DTO → ViewModel)
 * - NO business logic (backend validates bids, determines eligibility)
 * - Formatting and UI calculations only
 * - Single responsibility: prepare bid data for display
 */

import { serviceProviderService } from '@/services/service-provider.service';
import type { PlanBidWithPricing, Plan } from '@calwmp/shared';
import type {
  BidCardViewModel,
  BidDetailViewModel,
  BidListViewModel,
  BidFiltersViewModel,
  BidFormViewModel,
} from './view-models/BidViewModel';

/**
 * Bid Application Service
 * Handles bid data transformation and list management
 */
export class BidApplicationService {
  /**
   * Get bid list with filters
   */
  async getBidList(filters?: {
    status?: string;
    dateRange?: string;
  }): Promise<BidListViewModel> {
    const response = await serviceProviderService.getBids({
      status: filters?.status === 'all' ? undefined : filters?.status,
      limit: 100,
    });

    // Transform to view models
    const bidCards = response.bids.map((bid) => this.toBidCardViewModel(bid));

      // Build filters view model
      const filtersViewModel = this.buildFiltersViewModel(response.bids, filters);

      // Calculate status summary
      const statusSummary = this.calculateStatusSummary(response.bids);

      return {
        bids: bidCards,
        filters: filtersViewModel,
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalItems: bidCards.length,
          itemsPerPage: bidCards.length,
          hasNextPage: false,
          hasPreviousPage: false,
        },
        summary: {
          displayText: this.buildSummaryText(bidCards.length, response.total),
          byStatus: statusSummary,
        },
        isLoading: false,
        isEmpty: bidCards.length === 0,
        emptyStateMessage: this.getEmptyStateMessage(filters?.status),
        emptyStateAction: this.getEmptyStateAction(filters?.status),
      };
  }

  /**
   * Get single bid detail
   */
  async getBidDetail(bidId: string): Promise<BidDetailViewModel> {
    const response = await serviceProviderService.getBids({ limit: 100 });
    const bid = response.bids.find((b) => b.id === bidId);

    if (!bid) {
      throw new Error(`Bid ${bidId} not found`);
    }

    return this.toBidDetailViewModel(bid);
  }

  /**
   * Get bid form data for a plan
   */
  async getBidFormData(plan: Plan): Promise<BidFormViewModel> {
    // In production, would fetch service provider's default pricing
    return {
      planInfo: {
        planId: plan.id,
        projectName: 'N/A', // Parse from plan.data
        projectAddress: 'N/A', // Parse from plan.data
        estimatedWaste: 'N/A', // Parse from plan.data
        targetDiversionRate: 'N/A', // Parse from plan.data
      },
      defaultPricing: {
        haulingRate: 75.0,
        container10yd: 250.0,
        container20yd: 350.0,
        container30yd: 450.0,
      },
      validation: {
        minHaulingRate: 50.0,
        maxHaulingRate: 200.0,
        minContainerRate: 100.0,
        maxContainerRate: 1000.0,
        maxMessageLength: 500,
      },
      canSubmit: true,
      isSubmitting: false,
    };
  }

  /**
   * Transform bid DTO to card view model
   */
  private toBidCardViewModel(bid: PlanBidWithPricing): BidCardViewModel {
    const containerRates = this.extractContainerRates(bid.proposed_pricing);

    return {
      id: bid.id,
      planId: bid.plan_id,
      planDisplayId: this.shortenId(bid.plan_id),
      statusBadge: this.getStatusBadge(bid.status),
      projectName: `Plan ${this.shortenId(bid.plan_id)}`, // Backend should provide actual name
      projectAddress: 'N/A', // Backend should provide
      pricing: {
        haulingRate: bid.proposed_pricing.hauling_rate_per_ton
          ? this.formatCurrency(bid.proposed_pricing.hauling_rate_per_ton, 'ton')
          : undefined,
        containerRates,
        estimatedTotal: undefined, // Backend could provide total estimate
      },
      serviceDescription: bid.service_description,
      message: bid.message ?? undefined,
      submittedDate: this.formatDate(bid.created_at),
      submittedTime: this.formatTime(new Date(bid.created_at * 1000)),
      submittedRelative: this.formatRelativeTime(bid.created_at),
      lastUpdated: this.formatDate(bid.updated_at),
      canEdit: bid.status === 'pending',
      canWithdraw: bid.status === 'pending',
      canViewDetails: true,
      response: this.buildResponse(bid),
    };
  }

  /**
   * Transform bid DTO to detail view model
   */
  private toBidDetailViewModel(bid: PlanBidWithPricing): BidDetailViewModel {
    const card = this.toBidCardViewModel(bid);

    return {
      ...card,
      projectDetails: {
        projectType: 'N/A', // Backend should provide
        estimatedWaste: 'N/A', // Backend should provide
        targetDiversionRate: 'N/A', // Backend should provide
        startDate: 'N/A', // Backend should provide
        targetCompletionDate: undefined, // Backend should provide
      },
      fullPricing: this.buildFullPricing(bid.proposed_pricing),
      timeline: {
        submittedAt: this.formatTimestamp(bid.created_at),
        statusHistory: [
          {
            status: 'submitted',
            timestamp: this.formatTimestamp(bid.created_at),
            note: 'Bid submitted',
          },
          // Backend should provide full status history
        ],
      },
    };
  }

  // Helper methods for formatting

  private getStatusBadge(status: string): {
    label: string;
    color: string;
    icon: string;
  } {
    const statusMap: Record<string, { label: string; color: string; icon: string }> = {
      pending: { label: 'Pending', color: 'blue', icon: 'clock' },
      accepted: { label: 'Accepted', color: 'green', icon: 'check' },
      rejected: { label: 'Rejected', color: 'red', icon: 'x' },
      withdrawn: { label: 'Withdrawn', color: 'gray', icon: 'arrow-left' },
      awarded: { label: 'Awarded', color: 'green', icon: 'trophy' },
    };

    return (
      statusMap[status] || {
        label: status.charAt(0).toUpperCase() + status.slice(1),
        color: 'gray',
        icon: 'info',
      }
    );
  }

  private extractContainerRates(
    pricing: PlanBidWithPricing['proposed_pricing']
  ): Array<{ size: string; rate: string }> {
    const rates: Array<{ size: string; rate: string }> = [];

    if (pricing.bin_rental_10yd) {
      rates.push({
        size: '10yd',
        rate: this.formatCurrency(pricing.bin_rental_10yd),
      });
    }

    if (pricing.bin_rental_20yd) {
      rates.push({
        size: '20yd',
        rate: this.formatCurrency(pricing.bin_rental_20yd),
      });
    }

    if (pricing.bin_rental_30yd) {
      rates.push({
        size: '30yd',
        rate: this.formatCurrency(pricing.bin_rental_30yd),
      });
    }

    if (pricing.bin_rental_40yd) {
      rates.push({
        size: '40yd',
        rate: this.formatCurrency(pricing.bin_rental_40yd),
      });
    }

    return rates;
  }

  private buildFullPricing(
    pricing: PlanBidWithPricing['proposed_pricing']
  ): BidDetailViewModel['fullPricing'] {
    const containerRentals: Array<{
      size: string;
      quantity: number;
      unitPrice: number;
      displayText: string;
      totalCost: string;
    }> = [];

    // Build container rentals list
    const containerSizes = [
      { key: 'bin_rental_10yd' as const, size: '10yd' },
      { key: 'bin_rental_20yd' as const, size: '20yd' },
      { key: 'bin_rental_30yd' as const, size: '30yd' },
      { key: 'bin_rental_40yd' as const, size: '40yd' },
    ];

    for (const { key, size } of containerSizes) {
      const rate = pricing[key];
      if (rate) {
        containerRentals.push({
          size,
          quantity: 1, // Default quantity
          unitPrice: rate,
          displayText: `1x ${size} @ ${this.formatCurrency(rate)} each`,
          totalCost: this.formatCurrency(rate),
        });
      }
    }

    // Calculate subtotal
    const subtotal = containerRentals.reduce((sum, item) => sum + item.unitPrice, 0);

    return {
      haulingRate: pricing.hauling_rate_per_ton
        ? {
            amount: pricing.hauling_rate_per_ton,
            displayText: `${this.formatCurrency(pricing.hauling_rate_per_ton)} per ton`,
            estimatedTons: undefined, // Backend should provide
            estimatedCost: undefined, // Backend should calculate
          }
        : undefined,
      containerRentals,
      additionalServices: [], // Backend should provide
      summary: {
        subtotal: this.formatCurrency(subtotal),
        fees: undefined, // Backend should provide
        total: this.formatCurrency(subtotal),
      },
    };
  }

  private buildResponse(
    bid: PlanBidWithPricing
  ): BidCardViewModel['response'] | undefined {
    if (bid.status === 'accepted' || bid.status === 'rejected') {
      return {
        status: bid.status,
        message: undefined, // Backend should provide rejection reason
        respondedAt: this.formatTimestamp(bid.updated_at),
      };
    }
    return undefined;
  }

  private buildFiltersViewModel(
    allBids: PlanBidWithPricing[],
    activeFilters?: { status?: string; dateRange?: string }
  ): BidFiltersViewModel {
    const statusCounts = this.calculateStatusSummary(allBids);

    return {
      statuses: [
        {
          value: 'all',
          label: 'All Bids',
          count: allBids.length,
          isActive: !activeFilters?.status || activeFilters.status === 'all',
        },
        {
          value: 'pending',
          label: 'Pending',
          count: statusCounts.pending,
          isActive: activeFilters?.status === 'pending',
        },
        {
          value: 'accepted',
          label: 'Accepted',
          count: statusCounts.accepted,
          isActive: activeFilters?.status === 'accepted',
        },
        {
          value: 'rejected',
          label: 'Rejected',
          count: statusCounts.rejected,
          isActive: activeFilters?.status === 'rejected',
        },
        {
          value: 'withdrawn',
          label: 'Withdrawn',
          count: statusCounts.withdrawn,
          isActive: activeFilters?.status === 'withdrawn',
        },
      ],
      dateRanges: [
        {
          value: 'all',
          label: 'All Time',
          isActive: !activeFilters?.dateRange || activeFilters.dateRange === 'all',
        },
        { value: 'today', label: 'Today', isActive: activeFilters?.dateRange === 'today' },
        { value: 'week', label: 'This Week', isActive: activeFilters?.dateRange === 'week' },
        { value: 'month', label: 'This Month', isActive: activeFilters?.dateRange === 'month' },
      ],
    };
  }

  private calculateStatusSummary(bids: PlanBidWithPricing[]): {
    pending: number;
    accepted: number;
    rejected: number;
    withdrawn: number;
  } {
    return bids.reduce(
      (acc, bid) => {
        const status = bid.status as keyof typeof acc;
        if (status in acc) {
          acc[status]++;
        }
        return acc;
      },
      { pending: 0, accepted: 0, rejected: 0, withdrawn: 0 }
    );
  }

  // Formatting utilities

  private formatCurrency(amount: number, unit?: string): string {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);

    return unit ? `${formatted}/${unit}` : formatted;
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private formatTime(date: Date): string {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  private formatTimestamp(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  private formatRelativeTime(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      return 'Just now';
    } else if (diffHours < 24) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else {
      return this.formatDate(timestamp);
    }
  }

  private shortenId(id: string): string {
    if (id.length <= 8) return id;
    return `${id.slice(0, 4)}...${id.slice(-4)}`;
  }

  private buildSummaryText(filtered: number, total: number): string {
    if (filtered === total) {
      return `Showing all ${total} ${total === 1 ? 'bid' : 'bids'}`;
    } else {
      return `Showing ${filtered} of ${total} ${total === 1 ? 'bid' : 'bids'}`;
    }
  }

  private getEmptyStateMessage(status?: string): string {
    if (!status || status === 'all') {
      return 'No bids yet. Submit your first bid on an available job to get started.';
    }
    return `No ${status} bids found.`;
  }

  private getEmptyStateAction(status?: string): { label: string; url: string } | undefined {
    if (!status || status === 'all') {
      return {
        label: 'Browse Available Jobs',
        url: '/provider/dashboard?tab=available',
      };
    }
    return undefined;
  }
}

// Export singleton instance
export const bidApplicationService = new BidApplicationService();
